import { CalendarEvent, EVENT_TYPE_ICONS, EVENT_TYPE_LABELS } from '../../../hooks/useCalendarEvents';

// ── Status dot helper ────────────────────────────────────────────────────────
// Events that don't represent a payable/receivable transaction
const NON_FINANCIAL_STATUSES = new Set(['active', 'done', 'cancelled']);

export interface StatusDotInfo {
  color: string;
  label: string;
  show: boolean;
}

export function getStatusDot(event: CalendarEvent): StatusDotInfo {
  // card_closing and goal_deadline have status = 'active' → no dot
  if (!event.status || NON_FINANCIAL_STATUSES.has(event.status)) {
    return { color: '', label: '', show: false };
  }

  // Paid / received → green
  if (event.status === 'pago' || event.status === 'recebido') {
    return { color: '#22c55e', label: 'Pago', show: true };
  }

  // Overdue: date < today and not paid
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.start_date + 'T00:00:00');
  if (eventDate < today) {
    return { color: '#ef4444', label: 'Atrasado', show: true };
  }

  // Pending
  return { color: '#f97316', label: 'Pendente', show: true };
}

// ── StatusDot component ──────────────────────────────────────────────────────
interface StatusDotProps {
  event: CalendarEvent;
  size?: 'sm' | 'md';
}

export const StatusDot = ({ event, size = 'md' }: StatusDotProps) => {
  const dot = getStatusDot(event);
  if (!dot.show) return null;

  const sizeClass = size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2';

  return (
    <span
      className={`${sizeClass} rounded-full shrink-0 inline-block`}
      style={{ backgroundColor: dot.color }}
      title={dot.label}
    />
  );
};

// ── EventBadge ───────────────────────────────────────────────────────────────
interface EventBadgeProps {
  event: CalendarEvent;
  compact?: boolean;
  onClick?: () => void;
}

export const EventBadge = ({ event, compact = false, onClick }: EventBadgeProps) => {
  const icon = EVENT_TYPE_ICONS[event.event_type] ?? 'ri-calendar-line';
  const label = EVENT_TYPE_LABELS[event.event_type] ?? 'Evento';

  if (compact) {
    return (
      <button
        onClick={onClick}
        className="w-full text-left flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-500 cursor-pointer transition-opacity hover:opacity-80 whitespace-nowrap"
        style={{ backgroundColor: event.color + '22', color: event.color }}
        title={event.title}
      >
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ backgroundColor: event.color }}
        />
        <span className="truncate flex-1 min-w-0">{event.title}</span>
        <StatusDot event={event} size="sm" />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all cursor-pointer hover:shadow-sm group"
      style={{ borderColor: event.color + '30', backgroundColor: event.color + '08' }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: event.color + '20' }}
      >
        <i className={`${icon} text-[15px]`} style={{ color: event.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[13px] font-600 text-gray-800 truncate">{event.title}</span>
          {!event.is_manual && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-500 shrink-0"
              style={{ backgroundColor: event.color + '20', color: event.color }}
            >
              {label}
            </span>
          )}
          {event.is_manual && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-500 bg-indigo-50 text-indigo-500 shrink-0">
              Manual
            </span>
          )}
        </div>
        {event.description && (
          <p className="text-[12px] text-gray-400 truncate mt-0.5">{event.description}</p>
        )}
      </div>
      {event.amount != null && (
        <span className="text-[12px] font-600 shrink-0" style={{ color: event.color }}>
          R$ {Number(event.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )}
      <StatusDot event={event} size="md" />
      {event.is_manual && (
        <i className="ri-more-2-line text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity text-[15px] shrink-0" />
      )}
    </button>
  );
};
